import { z } from "zod";

/**
 * TIN validation helpers
 * All TIN types require exactly 9 digits
 */
const validateTIN = (value) => {
  const digitsOnly = value.replace(/\D/g, '');
  return digitsOnly.length === 9;
};

const POSTAL_CODE_REGEX = /^[a-zA-Z0-9][a-zA-Z0-9\s-]{1,14}[a-zA-Z0-9]$/;
const POSTAL_CODE_MESSAGE = "Postal code must be 3 to 16 letters/numbers, spaces, or hyphens";

// Tax Information Form Schema
export const taxInfoSchema = z.object({
  firstName: z
    .string()
    .min(1, "First name is required")
    .min(2, "First name must be at least 2 characters")
    .max(50, "First name must be less than 50 characters")
    .regex(/^[a-zA-Z\s'-]+$/, "First name can only contain letters, spaces, hyphens, and apostrophes"),

  lastName: z
    .string()
    .min(1, "Last name is required")
    .min(2, "Last name must be at least 2 characters")
    .max(50, "Last name must be less than 50 characters")
    .regex(/^[a-zA-Z\s'-]+$/, "Last name can only contain letters, spaces, hyphens, and apostrophes"),

  businessName: z
    .string()
    .optional()
    .refine(
      (val) => !val || val.length >= 2,
      "Business name must be at least 2 characters if provided"
    )
    .refine(
      (val) => !val || val.length <= 100,
      "Business name must be less than 100 characters"
    ),

  federalTaxClassification: z
    .string()
    .min(1, "Federal tax classification is required"),

  otherTaxClassification: z
    .string()
    .optional(),

  address: z
    .string()
    .min(1, "Address is required")
    .min(5, "Address must be at least 5 characters")
    .max(200, "Address must be less than 200 characters"),

  city: z
    .string()
    .min(1, "City is required")
    .min(2, "City must be at least 2 characters")
    .max(50, "City must be less than 50 characters")
    .regex(/^[a-zA-Z\s'-]+$/, "City can only contain letters, spaces, hyphens, and apostrophes"),

  state: z
    .string()
    .min(1, "State is required"),

  zipCode: z
    .string()
    .min(1, "ZIP/Postal code is required")
    .min(3, "ZIP/Postal code must be at least 3 characters")
    .max(16, "ZIP/Postal code must be 16 characters or less")
    .regex(POSTAL_CODE_REGEX, POSTAL_CODE_MESSAGE),

  tinType: z
    .string()
    .min(1, "TIN type is required"),

  tinNumber: z
    .string()
    .min(1, "Taxpayer Identification Number is required"),

  // signature: z
  //   .string()
  //   .min(1, "Digital signature is required")
  //   .refine(
  //     (val) => val && val.length > 100 && val.startsWith("data:image"),
  //     {
  //       message: "Please provide a valid digital signature"
  //     }
  //   ),

  // date: z
  //   .string()
  //   .min(1, "Date is required")
  //   .refine(
  //     (val) => {
  //       const date = new Date(val);
  //       const today = new Date();
  //       today.setHours(0, 0, 0, 0);
  //       return date <= today;
  //     },
  //     {
  //       message: "Date cannot be in the future"
  //     }
  //   ),

  certification: z
    .boolean()
    .refine((val) => val === true, "You must certify that the information is true and correct"),
})
.superRefine((data, ctx) => {
  // Validate otherTaxClassification when federalTaxClassification is 'Other'
  if (data.federalTaxClassification === 'Other') {
    if (!data.otherTaxClassification || data.otherTaxClassification.trim().length < 2) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Please specify the tax classification",
        path: ["otherTaxClassification"]
      });
    }
  }

  // Validate tinNumber based on tinType
  if (data.tinType && data.tinNumber) {
    const isValid = validateTIN(data.tinNumber);

    if (!isValid) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Invalid TIN format. Please enter a valid 9-digit number",
        path: ["tinNumber"]
      });
    }
  }
});

/**
 * Formats error messages from API response into a user-friendly HTML string
 * @param {Object} response - The API response object
 * @returns {string} - Formatted error message as HTML string
 */
export const formatErrorResponse = (response) => {
  // Default fallback message
  const defaultMessage = 'Failed to submit W-9 form. Please try again.';
  
  // Extract base error message
  const baseMessage = response?.message || response?.error || defaultMessage;
  
  // Check if there are validation errors in the errors array
  if (response?.errors && Array.isArray(response.errors) && response.errors.length > 0) {
    // Filter out empty/null errors and extract error messages
    const validErrors = response.errors
      .filter(error => error != null)
      .map(error => {
        // Handle different error object structures
        const errorText = error.Message || error.message || error.error || String(error);
        // Escape HTML to prevent XSS (basic sanitization)
        return errorText
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;')
          .replace(/'/g, '&#x27;');
      })
      .filter(msg => msg.trim().length > 0);
    
    if (validErrors.length > 0) {
      // Create formatted list of errors
      const errorsList = validErrors
        .map(error => `<li style="margin-bottom: 8px;">${error}</li>`)
        .join('');
      
      // Combine base message with detailed errors
      const formattedErrors = `
        <div style="text-align: left;">
          <ul style="margin: 0; padding-left: 20px; list-style-type: disc;">
            ${errorsList}
          </ul>
        </div>
      `;
      
      return formattedErrors;
    }
  }
  
  // Return base message if no detailed errors found
  return baseMessage;
};

/**
 * W-8 Tax Information Form Schema
 * Aligned with TaxBandits API v1.7.3 (Form W-8BEN):
 * https://developer.taxbandits.com/docs/formw8ben/requestbyurl/
 * https://developer.taxbandits.com/docs/FormW8BEN/Webhook
 * RequestByUrl/FormData field sizes: Name ..40, Address1/2 ..50, City ..50,
 * ProvinceOrStateNm ..50, Country 2, PostalCd ..16.
 */
export const w8TaxInfoSchema = z.object({
  // Identification of Beneficial Owner (TaxBandits: NmOfIndividual / Name ..40)
  firstName: z
    .string()
    .min(1, "First name is required")
    .min(2, "First name must be at least 2 characters")
    .max(40, "First name must be 40 characters or less")
    .regex(/^[a-zA-Z\s'-]+$/, "First name can only contain letters, spaces, hyphens, and apostrophes"),

  lastName: z
    .string()
    .min(1, "Last name is required")
    .min(2, "Last name must be at least 2 characters")
    .max(40, "Last name must be 40 characters or less")
    .regex(/^[a-zA-Z\s'-]+$/, "Last name can only contain letters, spaces, hyphens, and apostrophes"),

  countryOfCitizenship: z
    .string()
    .min(1, "Country of citizenship is required"),

  otherCountryOfCitizenship: z
    .string()
    .optional(),

  // Permanent Residence Address (TaxBandits: PermanentAddress - Address1 ..50, City ..50, State ..50, PostalCd ..16)
  permanentStreetAddress: z
    .string()
    .min(1, "Street address is required")
    .min(5, "Street address must be at least 5 characters")
    .max(50, "Street address must be 50 characters or less"),

  permanentCity: z
    .string()
    .min(1, "City is required")
    .min(2, "City must be at least 2 characters")
    .max(50, "City must be 50 characters or less"),

  permanentStateProvince: z
    .string()
    .min(1, "State/Province is required")
    .max(50, "State/Province must be 50 characters or less"),

  permanentPostalCode: z
    .string()
    .min(1, "Postal code is required")
    .min(3, "Postal code must be at least 3 characters")
    .max(16, "Postal code must be 16 characters or less")
    .regex(POSTAL_CODE_REGEX, POSTAL_CODE_MESSAGE),

  permanentCountry: z
    .string()
    .min(1, "Country is required"),

  // Mailing Address (if different) - Optional
  mailingStreetAddress: z
    .string()
    .optional(),

  mailingCity: z
    .string()
    .optional(),

  mailingStateProvince: z
    .string()
    .optional(),

  mailingPostalCode: z
    .string()
    .optional(),

  mailingCountry: z
    .string()
    .optional(),

  // Tax Identification
  usTaxpayerId: z
    .string()
    .optional(),

  foreignTaxId: z
    .string()
    .optional(),

  ftinNotRequired: z
    .boolean()
    .optional(),

  referenceNumber: z
    .string()
    .optional(),

  // Claim of Tax Treaty Benefits
  countryOfResidenceForTaxTreaty: z
    .string()
    .min(1, "Country of Residence for Tax Treaty is required"),

  otherCountryOfResidenceForTaxTreaty: z
    .string()
    .optional(),

  articleAndParagraph: z
    .string()
    .optional(),

  rateOfWithholding: z
    .number()
    .min(0, "Rate of withholding must be 0 or greater")
    .max(100, "Rate of withholding cannot exceed 100%")
    .optional(),

  // Type of Income and Additional Conditions
  typeOfIncome: z
    .string()
    .optional(),

  additionalConditionsForTreatyBenefits: z
    .string()
    .optional(),

  // Digital Signature and Date (TaxBandits: Signature.SignerNm, CapacityInWhichActing)
  digitalSignature: z
    .string()
    .min(1, "Digital signature is required")
    .min(2, "Digital signature must be at least 2 characters"),

  signatureDate: z
    .string()
    .min(1, "Date is required"),

  capacityInWhichActing: z
    .string()
    .optional(),

  certification: z
    .boolean()
    .refine((val) => val === true, "You must certify that the information is true and correct"),
})
.superRefine((data, ctx) => {
  // TaxBandits: Recipient Name size ..40 (combined first + last)
  const fullName = `${(data.firstName || '').trim()} ${(data.lastName || '').trim()}`.trim();
  if (fullName.length > 40) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Full name (first + last) must be 40 characters or less",
      path: ["firstName"]
    });
  }

  // Validate otherCountryOfCitizenship when countryOfCitizenship is 'OTHER'
  if (data.countryOfCitizenship === 'OTHER') {
    if (!data.otherCountryOfCitizenship || data.otherCountryOfCitizenship.trim().length < 2) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Please specify the country name",
        path: ["otherCountryOfCitizenship"]
      });
    }
  }

  // Mailing address section: only validate if user has started filling any mailing field.
  // Each section (permanent vs mailing) validates independently.
  const mailingFields = [
    data.mailingStreetAddress,
    data.mailingCity,
    data.mailingStateProvince,
    data.mailingPostalCode,
    data.mailingCountry
  ];
  const hasMailingAddress = mailingFields.some(
    (val) => val != null && String(val).trim().length > 0
  );

  if (hasMailingAddress) {
    const street = (data.mailingStreetAddress || '').trim();
    const city = (data.mailingCity || '').trim();
    const state = (data.mailingStateProvince || '').trim();
    const postal = (data.mailingPostalCode || '').trim();
    const country = (data.mailingCountry || '').trim();

    if (street.length < 5) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Mailing street address is required and must be at least 5 characters",
        path: ["mailingStreetAddress"]
      });
    } else if (street.length > 50) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Mailing street address must be 50 characters or less",
        path: ["mailingStreetAddress"]
      });
    }
    if (city.length < 2) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Mailing city is required and must be at least 2 characters",
        path: ["mailingCity"]
      });
    } else if (city.length > 50) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Mailing city must be 50 characters or less",
        path: ["mailingCity"]
      });
    }
    if (state.length < 1) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Mailing state/province is required",
        path: ["mailingStateProvince"]
      });
    } else if (state.length > 50) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Mailing state/province must be 50 characters or less",
        path: ["mailingStateProvince"]
      });
    }
    if (postal.length < 3) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Mailing postal code is required and must be at least 3 characters",
        path: ["mailingPostalCode"]
      });
    } else if (postal.length > 16) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Mailing postal code must be 16 characters or less",
        path: ["mailingPostalCode"]
      });
    } else if (!POSTAL_CODE_REGEX.test(postal)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: POSTAL_CODE_MESSAGE,
        path: ["mailingPostalCode"]
      });
    }
    if (country.length < 1) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Mailing country is required",
        path: ["mailingCountry"]
      });
    }
  }

  // Validate foreignTaxId - required only when "FTIN not legally required" is NOT checked
  const ftinNotRequiredChecked = data.ftinNotRequired === true;
  const hasForeignTaxId = (data.foreignTaxId || '').trim().length > 0;
  if (!ftinNotRequiredChecked && !hasForeignTaxId) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Foreign Tax Identifying Number is required",
      path: ["foreignTaxId"]
    });
  }

  // Validate countryOfResidenceForTaxTreaty when 'OTHER' is selected
  if (data.countryOfResidenceForTaxTreaty === 'OTHER') {
    if (!data.otherCountryOfResidenceForTaxTreaty || data.otherCountryOfResidenceForTaxTreaty.trim().length < 2) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Please specify the country name",
        path: ["otherCountryOfResidenceForTaxTreaty"]
      });
    }
  }

  // Validate signatureDate format (dd/mm/yyyy)
  if (data.signatureDate) {
    const datePattern = /^(\d{2})\s*\/\s*(\d{2})\s*\/\s*(\d{4})$/;
    if (!datePattern.test(data.signatureDate)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Date must be in format dd / mm / yyyy",
        path: ["signatureDate"]
      });
    }
  }
});

